import { supabase } from "@/integrations/supabase/client";
import { QuizQuestion } from "@/components/admin/lesson/types";

export const updateLessonQuestions = async (lessonId: string, questions: QuizQuestion[]) => {
  try {
    console.log('Starting question update for lesson:', lessonId, 'with questions:', questions);

    // 1. Obtener las preguntas existentes
    const { data: existingQuestions, error: fetchError } = await supabase
      .from('quiz_questions')
      .select('id, question')
      .eq('lesson_id', lessonId);

    if (fetchError) {
      console.error('Error fetching existing questions:', fetchError);
      throw new Error('Error al obtener las preguntas existentes');
    }

    // 2. Preparar las nuevas preguntas válidas
    const validQuestions = questions
      .filter(q => 
        q.question?.trim() && 
        q.correct_answer?.trim() && 
        Array.isArray(q.wrong_answers) && 
        q.wrong_answers.length > 0 && // Solo requerimos al menos una respuesta incorrecta
        q.wrong_answers.every((a: string) => a && a.trim()) // Aseguramos que todas las respuestas tengan contenido
      )
      .map(q => ({
        lesson_id: lessonId,
        question: q.question.trim(),
        correct_answer: q.correct_answer.trim(),
        wrong_answers: q.wrong_answers.map((a: string) => a.trim()),
        active: true,
        points: q.points || 10,
        ...(q.id && { id: q.id }) // Solo incluir id si existe
      }));

    console.log('Valid questions after filtering:', validQuestions);

    // 3. Separar preguntas en nuevas y existentes
    const questionsToUpdate = validQuestions.filter(q => q.id);
    const questionsToCreate = validQuestions.filter(q => !q.id);
    const questionIdsToKeep = new Set(validQuestions.map(q => q.id).filter(Boolean));

    console.log('Questions to update:', questionsToUpdate);
    console.log('Questions to create:', questionsToCreate);

    // 4. Eliminar preguntas que ya no existen
    const questionIdsToDelete = existingQuestions
      ?.filter(q => !questionIdsToKeep.has(q.id))
      .map(q => q.id);

    if (questionIdsToDelete?.length > 0) {
      console.log('Questions to delete:', questionIdsToDelete);
      const { error: deleteError } = await supabase
        .from('quiz_questions')
        .delete()
        .in('id', questionIdsToDelete);

      if (deleteError) {
        console.error('Error deleting old questions:', deleteError);
        throw new Error('Error al eliminar preguntas antiguas');
      }
    }

    // 5. Actualizar preguntas existentes
    for (const question of questionsToUpdate) {
      const { error: updateError } = await supabase
        .from('quiz_questions')
        .update({
          question: question.question,
          correct_answer: question.correct_answer,
          wrong_answers: question.wrong_answers,
          active: question.active,
          points: question.points
        })
        .eq('id', question.id);

      if (updateError) {
        console.error('Error updating question:', updateError);
        throw new Error('Error al actualizar una pregunta');
      }
    }

    // 6. Crear nuevas preguntas
    if (questionsToCreate.length > 0) {
      const { error: createError } = await supabase
        .from('quiz_questions')
        .insert(questionsToCreate.map(q => ({
          lesson_id: q.lesson_id,
          question: q.question,
          correct_answer: q.correct_answer,
          wrong_answers: q.wrong_answers,
          active: q.active,
          points: q.points
        })));

      if (createError) {
        console.error('Error creating new questions:', createError);
        throw new Error('Error al crear nuevas preguntas');
      }
    }

    return true;
  } catch (error: any) {
    console.error('Error in updateLessonQuestions:', error);
    throw error;
  }
};